import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FIELDS = [
  ["company_name", "Company Name", true],
  ["trade_name", "Trade Name", false],
  ["gst", "GST Number", false],
  ["pan", "PAN", false],
  ["address", "Company Address", false],
  ["contact_person", "Contact Person", false],
  ["mobile", "Mobile Number", false],
  ["whatsapp", "WhatsApp Number", true],
  ["email", "Email", false],
  ["stock_location", "Stock Location", false],
  ["years_in_business", "Years in Business", false],
];

export default function CompleteProfile({ embedded }) {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [form, setForm] = useState({ whatsapp: user?.mobile || "", mobile: user?.mobile || "" });
  const [busy, setBusy] = useState(false);
  const [localityCustom, setLocalityCustom] = useState(false);

  useEffect(() => {
    api.get("/cities?active=true").then(({ data }) => setCities(data));
    api.get("/profile").then(({ data }) => { if (data && data.company_name) setForm(data); });
  }, []);

  useEffect(() => {
    if (form.city) api.get(`/localities?city=${encodeURIComponent(form.city)}`).then(({ data }) => setLocalities(data.filter((l) => l.active)));
  }, [form.city]);

  useEffect(() => { if (form.locality && localities.length && !localities.some((l) => l.name === form.locality)) setLocalityCustom(true); }, [form.locality, localities]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.whatsapp || !form.city) { toast.error("Company Name, WhatsApp and City are required"); return; }
    setBusy(true);
    try {
      if (form.locality && form.city && !localities.some((l) => l.name === form.locality)) {
        await api.post("/localities", { city: form.city, name: form.locality }).catch(() => {});
        const { data } = await api.get(`/localities?city=${encodeURIComponent(form.city)}`);
        setLocalities(data.filter((l) => l.active));
        setLocalityCustom(false);
      }
      await api.put("/profile", { ...form, years_in_business: Number(form.years_in_business) || 0 });
      await refresh();
      toast.success("Company profile saved");
      if (!embedded) nav("/seller/subscription");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-secondary/40 py-10 px-6"}>
      <div className={embedded ? "" : "max-w-2xl mx-auto"}>
        <div className="bg-card border rounded-2xl p-6 sm:p-8 shadow-sm">
          {!embedded && (
            <>
              <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center mb-4"><Building2 className="h-6 w-6 text-primary" /></div>
              <h1 className="font-heading text-2xl font-extrabold">Complete Your Company Profile</h1>
              <p className="text-muted-foreground text-sm font-medium mb-6">This helps buyers and admin identify your business.</p>
            </>
          )}
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
              {FIELDS.slice(0, 4).map(([k, label, req]) => (
                <div key={k}>
                  <Label>{label}{req && " *"}</Label>
                  <Input data-testid={`profile-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} required={req} />
                </div>
              ))}
            </div>

            <div>
              <Label>Company Address</Label>
              <Input data-testid="profile-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div>
              <Label>City *</Label>
              <Select value={form.city || ""} onValueChange={(v) => set("city", v)}>
                <SelectTrigger data-testid="profile-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Locality</Label>
              <Select
                value={localityCustom ? "__custom__" : (form.locality || "")}
                onValueChange={(v) => { if (v === "__custom__") { setLocalityCustom(true); set("locality", ""); } else { setLocalityCustom(false); set("locality", v); } }}
              >
                <SelectTrigger data-testid="profile-locality"><SelectValue placeholder="Select locality" /></SelectTrigger>
                <SelectContent>
                  {localities.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                  <SelectItem value="__custom__">➕ Add my locality…</SelectItem>
                </SelectContent>
              </Select>
              {localityCustom && (
                <Input data-testid="profile-locality-custom" className="mt-2" value={form.locality || ""} onChange={(e) => set("locality", e.target.value)} placeholder="Type your locality" />
              )}
            </div>
            <div>
              <Label>Stock Location</Label>
              <Input data-testid="profile-stock_location" value={form.stock_location || ""} onChange={(e) => set("stock_location", e.target.value)} />
            </div>

            {FIELDS.slice(5).map(([k, label, req]) => (
              <div key={k}>
                <Label>{label}{req && " *"}</Label>
                <Input data-testid={`profile-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
                  type={k === "years_in_business" ? "number" : "text"} required={req} />
              </div>
            ))}

            <div className="sm:col-span-2">
              <Button data-testid="profile-submit" type="submit" disabled={busy} className="w-full font-bold h-11">
                {busy ? "Saving…" : embedded ? "Save Profile" : "Save & Continue to Subscription"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
